import { QA_API_DOMAIN, fetchWithSupabaseAuth } from "@/api/classifier";
import { WRITE_BACK_SEND_TOAST_KIND, WriteBackSendResultSchema } from "@/api/writeBackManagement";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { z } from "zod";

const EnqueueHumanReviewSchema = z.object({
  encounter_id: z.uuid(),
  human_review_id: z.uuid(),
});

export type EnqueueHumanReviewRequest = z.infer<typeof EnqueueHumanReviewSchema>;

const EnqueueResultRequestSchema = z.object({
  inbound_classification_result_id: z.uuid(),
  human_review_id: z.uuid().optional(),
});

export type EnqueueResultRequest = z.infer<typeof EnqueueResultRequestSchema>;

const RejectResultRequestSchema = z.object({
  encounter_id: z.uuid(),
});

export type RejectResultRequest = z.infer<typeof RejectResultRequestSchema>;

export function useWriteBackActions() {
  const writeBackHumanReviewMutation = useMutation({
    mutationFn: async (data: EnqueueHumanReviewRequest) => {
      const validated = EnqueueHumanReviewSchema.parse(data);

      const res = await fetchWithSupabaseAuth(`${QA_API_DOMAIN}/write-back/enqueue-human-review`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(validated),
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${res.status}: Failed to enqueue human review`);
      }

      return WriteBackSendResultSchema.parse(await res.json());
    },
    onSuccess: (result) => {
      toast[WRITE_BACK_SEND_TOAST_KIND[result.outcome]](result.message);
    },
    onError: (error: Error) => {
      console.error("Enqueue human review error:", error);
      toast.error(error instanceof Error ? error.message : "Failed to submit human review");
    },
  });

  const writeBackClassificationResultMutation = useMutation({
    mutationFn: async (data: EnqueueResultRequest) => {
      const validated = EnqueueResultRequestSchema.parse(data);

      const res = await fetchWithSupabaseAuth(`${QA_API_DOMAIN}/write-back/enqueue-result`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(validated),
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${res.status}: Failed to enqueue classification result`);
      }

      return WriteBackSendResultSchema.parse(await res.json());
    },
    onSuccess: (result) => {
      toast[WRITE_BACK_SEND_TOAST_KIND[result.outcome]](result.message);
    },
    onError: (error: Error) => {
      console.error("Enqueue classification result error:", error);
      toast.error(error instanceof Error ? error.message : "Failed to submit classification result");
    },
  });

  const rejectResultMutation = useMutation({
    mutationFn: async (data: RejectResultRequest) => {
      const validated = RejectResultRequestSchema.parse(data);

      const res = await fetchWithSupabaseAuth(`${QA_API_DOMAIN}/write-back/reject-encounter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          encounter_id: validated.encounter_id,
        }),
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${res.status}: Failed to reject result`);
      }

      return res.json();
    },
    onSuccess: () => {
      toast.success("Result rejected successfully");
    },
    onError: (error: Error) => {
      console.error("Reject result error:", error);
      toast.error(error instanceof Error ? error.message : "Failed to reject result");
    },
  });

  return {
    writeBackHumanReviewMutation,
    writeBackClassificationResultMutation,
    rejectResultMutation,
  };
}
